/**
 * Game client entrypoint: build a dungeon, then play one.
 *
 * **The client opens on the Build screen.** A staged page has no battle server to
 * reach, so building is what there is to do on it: a palette of the catalogue, a ghost
 * that snaps to open ports, and a header counting what the geometry implies. Pressing
 * Play hands the viewport to the game, which is the path it has always been -- pick a
 * character from the pack, join the battle room carrying that choice as a few strings,
 * and see every other player rendered from theirs. Input and combat are still to be
 * specified, and so is playing the dungeon you just built: the server owns position and
 * loads no level, so Play still draws the sample.
 */
import './style.css';
import { createScene } from './engine/createScene.ts';
import { DungeonView } from './world/dungeonView.ts';
import { createSampleLevel, ENTRANCE_BLOCK } from './world/sampleLevel.ts';
import { loadBlockCatalogue } from './world/blockCatalogue.ts';
import { BuildScreen } from './build/buildScreen.ts';
import { BattleConnection } from './net/battleConnection.ts';
import { Hud } from './ui/hud.ts';
import { spawnAvatar, packManifest, type Avatar } from './character/localAvatar.ts';
import { RemoteAvatars } from './character/remoteAvatars.ts';
import { SelectScreen, type SelectionResult } from './character/selectScreen.ts';
import {
  encodeAppearance,
  encodeTints,
  resolveLevel,
  tileToWorld,
  type Cell,
} from '@dungeon-builder/shared';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

const canvas = document.querySelector<HTMLCanvasElement>('#render-canvas');
if (!canvas) throw new Error('#render-canvas is missing from index.html');

const { scene, camera } = createScene(canvas);
const hud = new Hud();

// A level names block ids and nothing else, so the catalogue has to be in hand before
// there is anything to draw or anything to offer in the palette.
const catalogue = await loadBlockCatalogue();
const dungeon = new DungeonView(scene);

// The sample is what Play draws, and it is resolved here rather than on the way in:
// `resolveLevel` is pure, so where the players stand can be worked out before anything
// is drawn, and the Build screen gets the viewport first.
const level = createSampleLevel(catalogue);
const resolved = resolveLevel(level, catalogue);

/**
 * Where the level's characters stand.
 *
 * The server integrates movement from its own origin and knows nothing about level
 * geometry yet -- level loading is still to be specified -- so the client treats
 * server coordinates as **relative to the level's spawn point**. Without that a player
 * at the server's (0, 0, 0) stands in the level's corner tile rather than in the room.
 * When the server starts loading levels this becomes its job and `worldFrom` goes away.
 *
 * A dungeon is entered through its entrance block (§5.5), so that block's own middle is
 * where its players stand -- no level marks a spawn tile any more, and a block is the
 * smallest thing a level says anything about.
 */
function blockCentre(cells: readonly Cell[]): { x: number; z: number } {
  if (cells.length === 0) return { x: 0, z: 0 };
  const total = cells
    .map((cell) => tileToWorld(cell.x, cell.z))
    .reduce((sum, point) => ({ x: sum.x + point.x, z: sum.z + point.z }), { x: 0, z: 0 });
  return { x: total.x / cells.length, z: total.z / cells.length };
}

const entrance =
  resolved.blocks.find((block) => block.definition.id === ENTRANCE_BLOCK) ?? resolved.blocks[0];
const spawnCell = entrance ? blockCentre(entrance.cells) : { x: 0, z: 0 };
const spawn = new Vector3(spawnCell.x, 0.25, spawnCell.z);
const worldFrom = (x: number, y: number, z: number): Vector3 =>
  new Vector3(spawn.x + x, spawn.y + y, spawn.z + z);
const VIEW_RADIUS = 4.5;

/**
 * Points the camera at a character without losing the framing.
 *
 * `ArcRotateCamera.setTarget` keeps the camera *position* and recomputes the radius
 * from it, so re-targeting as the character moves silently pulls the camera away --
 * following a character from the level centre to the origin left it 35 m back, staring
 * at empty floor. The radius has to be restored after every retarget.
 */
let framed: Vector3 | null = null;
function frame(position: Vector3): void {
  if (framed && Vector3.DistanceSquared(framed, position) < 1e-4) return;
  framed = position.clone();
  camera.setTarget(position.add(new Vector3(0, 1, 0)));
  camera.radius = VIEW_RADIUS;
}

const connection = new BattleConnection({
  onStatus: (status, detail) => hud.setConnection(status, detail),
  onState: (state) => {
    hud.setPlayers(state.players.size);
    remotes.sync(state);

    // The server owns position -- for this client as much as for its peers. Until
    // input exists the local avatar simply follows its own server record, which also
    // keeps it in the same place as everyone else rather than at the preview spot.
    const own = connection.sessionId ? state.players.get(connection.sessionId) : undefined;
    if (own && local) {
      local.root.position.copyFrom(worldFrom(own.x, own.y, own.z));
      local.root.rotation.y = own.yaw;
      frame(local.root.position);
    }
  },
  onWelcome: (payload) => console.log('[game-client] welcome', payload),
});

const remotes = new RemoteAvatars(scene, () => connection.sessionId, worldFrom);

// --- building ----------------------------------------------------------------

const buildRoot = document.querySelector<HTMLElement>('#build-ui');
const buildPalette = document.querySelector<HTMLElement>('#build-palette');
if (!buildRoot || !buildPalette) throw new Error('the Build screen is missing from index.html');

const build = new BuildScreen({
  scene,
  camera,
  catalogue,
  view: dungeon,
  host: buildPalette,
  root: buildRoot,
  header: {
    blocks: document.querySelector<HTMLElement>('#build-blocks'),
    connections: document.querySelector<HTMLElement>('#build-connections'),
    sealed: document.querySelector<HTMLElement>('#build-sealed'),
  },
  actions: {
    copy: document.querySelector<HTMLElement>('#build-copy'),
    paste: document.querySelector<HTMLElement>('#build-paste'),
    reset: document.querySelector<HTMLElement>('#build-new'),
  },
  status: document.querySelector<HTMLElement>('#build-status'),
});
build.show();

// The pack is fetched now so that Play is not a wait, but the select screen is only
// built when Play is pressed: constructing it previews a character at once, and an
// avatar standing in the middle of the board being built is not the board.
void packManifest().catch(() => undefined);

const selectHost = document.querySelector<HTMLElement>('#character-select');

/** Hands the viewport to the game: the sample dungeon, and a character to walk it. */
function play(): void {
  build.hide();
  dungeon.load(level, catalogue);
  camera.beta = Math.PI / 2.6;
  camera.lowerRadiusLimit = 1;
  framed = null;
  frame(spawn);
  openCharacterSelect();
}

document.querySelector<HTMLElement>('#build-play')?.addEventListener('click', play);

// --- character selection ------------------------------------------------------

let local: Avatar | null = null;
let previewing = false;

/**
 * Rebuilds the previewed character.
 *
 * Re-entrant on purpose: the select screen fires on every click, and assembling is
 * asynchronous, so a burst of edits must not leave two bodies in the scene.
 */
async function preview(selection: SelectionResult): Promise<void> {
  if (previewing) return;
  previewing = true;
  try {
    local?.dispose();
    local = await spawnAvatar(scene, {
      gender: selection.gender,
      stance: selection.stance,
      position: spawn.clone(),
      colourway: selection.colourway,
      tints: selection.tints,
      variantIds: selection.variantIds,
    });
    framed = null;
    frame(spawn);
  } catch (error) {
    console.error('[character] could not build the character', error);
    hud.setConnection('offline', error instanceof Error ? error.message : String(error));
  } finally {
    previewing = false;
  }
}

function openCharacterSelect(): void {
  if (!selectHost) return;
  selectHost.hidden = false;

  void packManifest()
    .then((manifest) => {
      const screen = new SelectScreen(selectHost, {
        manifest,
        onChange: (selection) => void preview(selection),
        onConfirm: (selection) => {
          screen.dispose();
          selectHost.hidden = true;
          // The character travels as a few strings; every client already has the pack,
          // so peers render it without any asset transfer.
          void connection.connect({
            levelId: level.id,
            displayName: 'guest',
            character: {
              gender: selection.gender,
              stance: selection.stance,
              packId: manifest.packId,
              appearance: encodeAppearance(selection.variantIds),
              tints: encodeTints(selection.tints),
              colourway: selection.colourway,
            },
          });
        },
      });
    })
    .catch((error: unknown) => {
      console.error('[character] no pack manifest', error);
      selectHost.innerHTML =
        '<p>No character pack.<br>Run <code>npm run pipeline -- pack starter</code>, then reload.</p>';
    });
}

window.addEventListener('beforeunload', () => void connection.dispose());

if (import.meta.hot) {
  // Dev-only handle: the scene can be inspected from the console and by the
  // screenshot harness. Stripped from production builds with the HMR block.
  Object.assign(window, {
    dungeon: {
      scene,
      camera,
      local: () => local,
      remotes,
      connection,
      spawnAvatar,
      spawn,
      play,
      // The board as drawn: the browser suite derives what *should* be on screen from
      // these through shared's own `resolveLevel`, rather than reimplementing it.
      level,
      catalogue,
      resolved,
      // The dungeon being built. A placement has no DOM handle, so the suite reads the
      // board back through here and projects a cell to click through `cellScreenPoint`,
      // exactly as the editor's own sandbox is driven.
      build: {
        state: () => build.state,
        document: () => build.document,
        cellScreenPoint: (cell: Cell) => build.cellScreenPoint(cell),
      },
    },
  });
  import.meta.hot.dispose(() => {
    build.dispose();
    dungeon.dispose();
    local?.dispose();
    remotes.dispose();
    void connection.dispose();
  });
}
